<template>
  <div class="voyage-data-container">
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <span
          class="voyage-data-title"
          v-bind="attrs"
          v-on="on"
        >
          Voyage Data
        </span>
      </template>

      <span>
        Voyage Data detail general voyage information. Mean data are displayed in
        spaces with varying ranges.
      </span>
    </v-tooltip>
    <div class="grid-container">
      <div class="grid-item">
        <LoadingDataRow
          :is-loading="overconsumptionDataLoading"
          icon="mdi-barrel"
          category="Overconsumption"
          :value="displayOverconsumption"
        />
        <LoadingDataRow
          :is-loading="overconsumptionDataLoading"
          icon="mdi-access-point-check"
          category="Completeness"
          :value="displayCompleteness"
        />
        <LoadingDataRow
          :is-loading="foulingDataLoading"
          icon="mdi-grass"
          category="Fouling"
          :value="displayFouling"
        />
        <SensorIssuesDialog />
      </div>
      <div class="grid-item">
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-flag"
          category="Departure Port"
          :value="displayDeparturePort"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-calendar-blank"
          category="Time"
          :value="displayDepartureTime"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-flag-checkered"
          category="Arrival Port"
          :value="displayArrivalPort"
        />

        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-map-clock-outline"
          category="ETA"
          :value="displayEtaNextPort"
        />
      </div>
      <div class="grid-item">
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-calendar-blank"
          category="Bunker Date"
          :value="displayBunkerDate"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-map-marker"
          category="Bunker Port"
          :value="displayBunkerPort"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-gas-station"
          :category="hfoLabel"
          :value="displayHfo"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-gas-station"
          category="MGO"
          :value="displayMgo"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-gas-station"
          :category="lfoLabel"
          :value="displayLfo"
        />
      </div>
      <div class="grid-item">
        <LoadingDataRow
          v-if="showDisplacementData"
          :is-loading="voyageDataLoading"
          icon="mdi-weight"
          category="Displacement"
          :value="displayDisplacement"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-waves"
          category="Mean Draft"
          :value="displayMeanDraft"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-timer-outline"
          category="Steam Time"
          :value="displayTotalSteamTime"
        />
        <LoadingDataRow
          :is-loading="voyageDataLoading"
          icon="mdi-map-marker-distance"
          category="Miles Covered"
          :value="displayTotalDistRun"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import LoadingDataRow from "../LoadingDataRow.vue";
import SensorIssuesDialog from "@/views/sensor_issues/Dialog.vue";
export default {
  name: "GeneralDataCard",
  components: {
    LoadingDataRow,
    SensorIssuesDialog,
  },
  props: {
    showDisplacementData: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState("vesselInformation/voyageModule", [
      "voyageDataLoading",
      "voyageDataError",
    ]),
    ...mapGetters("vesselInformation/foulingModule", ["displayFouling"]),
    ...mapGetters("vesselInformation/overconsumptionModule", [
      "displayOverconsumption",
      "displayCompleteness",
    ]),
    ...mapState("vesselInformation/overconsumptionModule", [
      "overconsumptionDataLoading",
      "overconsumptionDataError",
    ]),
    ...mapState("vesselInformation/foulingModule", [
      "foulingDataLoading",
      "foulingDataError",
    ]),
    ...mapGetters("vesselInformation/voyageModule", [
      "displayDeparturePort",
      "displayArrivalPort",
      "displayEtaNextPort",
      "displayBunkerDate",
      "displayBunkerPort",
      "displayHfo",
      "displayMgo",
      "displayLfo",
      "displayDisplacement",
      "displayTotalSteamTime",
      "displayTotalDistRun",
      "displayMeanDraft",
      "displayDepartureTime",
    ]),
    isCompany5() {
      return process.env.VUE_APP_COMPANY_ID === "5";
    },
    hfoLabel() {
      return this.isCompany5 ? "HSFO" : "HFO";
    },
    lfoLabel() {
      return this.isCompany5 ? "VLSFO" : "LFO";
    },
  },
};
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  row-gap: 5px;
  column-gap: 5px;
  height: 100%;
  width: 100%;
}

.voyage-data-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: var(--color-bg);
  padding: 10px;
  height: 100%;
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  overflow: auto;
}

.grid-item {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 10px;
  padding: 10px;
  overflow: auto;
  border: 1px solid var(--color-text);
}

.data-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  background-color: transparent;
}

.data-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.voyage-data-title {
  font-size: 18px;
  font-weight: bold;
  color: var(--color-text);
}
</style>
